import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { FaEnvelope, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import logo from "../img/logo.jpeg";

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // "success" or "error"
  const [isUnsubscribed, setIsUnsubscribed] = useState(false);

  // Check if email is provided in URL parameters
  useEffect(() => {
    const emailParam = searchParams.get("email");
    if (emailParam) {
      setEmail(emailParam);
    }
  }, [searchParams]);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleUnsubscribe = async () => {
    if (isLoading) return;

    if (!isValidEmail(email)) {
      setMessage("Please enter a valid email address.");
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setMessage("");

    try {
      // Send unsubscribe request to API
      await axios.post(`${process.env.REACT_APP_API_URL}/unsubscribe`, {
        email: email,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        ipAddress: await getClientIP()
      });

      setMessage("You have been successfully unsubscribed from our newsletter. You will no longer receive emails from us.");
      setMessageType("success");
      setIsUnsubscribed(true);
      setEmail("");
    } catch (err) {
      console.error("Unsubscribe error:", err);
      setMessage(err.response?.data?.message || "An error occurred while processing your unsubscribe request. Please try again or contact us directly.");
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  const getClientIP = async () => {
    try {
      const response = await axios.get("https://api.ipify.org?format=json");
      return response.data.ip;
    } catch (error) {
      console.error("Error getting IP:", error);
      return "unknown";
    }
  };

  const handleEmailChange = (value) => {
    setEmail(value);
    setMessage("");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-purple-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8">
        {/* Logo */}
        <div className="text-center mb-8">
          <img className="h-16 w-auto mx-auto mb-4" src={logo} alt="AngelWatch Logo" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Unsubscribe</h1>
          <p className="text-gray-600">We're sorry to see you go</p>
        </div>

        {!isUnsubscribed ? (
          <>
            {/* Email Input */}
            <div className="mb-6">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaEnvelope className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  placeholder="Enter your email address"
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Unsubscribe Button */}
            <button
              onClick={handleUnsubscribe}
              disabled={isLoading || !email}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing...
                </div>
              ) : (
                "Unsubscribe"
              )}
            </button>

            {/* Message Display */}
            {message && (
              <div className={`mt-4 p-4 rounded-md flex items-center ${messageType === "success"
                  ? "bg-green-50 border border-green-200 text-green-800"
                  : "bg-red-50 border border-red-200 text-red-800"
                }`}>
                {messageType === "success" ? (
                  <FaCheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                ) : (
                  <FaTimesCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                )}
                <span className="text-sm">{message}</span>
              </div>
            )}
          </>
        ) : (
          /* Success State */
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <FaCheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Successfully Unsubscribed</h3>
            <p className="text-sm text-gray-600 mb-6">
              You have been removed from our mailing list. You will no longer receive newsletters or promotional emails from AngelWatch.
            </p>
            <div className="space-y-3">
              <a
                href="/"
                className="block w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
              >
                Return to Homepage
              </a>
              <p className="text-xs text-gray-500">
                Changed your mind? You can always{" "}
                <a href="/contactUs" className="text-purple-600 hover:text-purple-500 underline">
                  contact us
                </a>{" "}
                to resubscribe.
              </p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-500">
            © 2025 AngelWatch LMS. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
